import { Badge, Box, Divider, Typography, Menu, MenuItem, ListItemIcon, Avatar, useTheme } from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { IconBuilding, IconHandshake } from "./LandingIcons";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../authentication/AuthProvider";
import { Notification } from "../db_access/notification";
import HeaderContainer from "../components/container/HeaderContainer";
import HeaderNavigationButton from "../pages/Header/HeaderNavigationButton";
import { useContextProvider } from "../components/page-context/ContextProvider";
import routing from "../routing";
import { LuChevronDown, LuSettings, LuShield, LuBell, LuLogOut, LuLifeBuoy, LuRefreshCw } from "react-icons/lu";
import { FiArrowLeft } from "react-icons/fi";
import { Company } from "../db_access/company";
import { clearUserCache } from "../utilities/queryClient";
import { M3_BRAND, getM3Colors } from "../theme/style/m3Palette";

function Header() {
  const { t } = useTranslation();
  const theme = useTheme();
  const m3 = getM3Colors(theme.palette.mode === "dark");
  const { user, username, companyName, attemptLogout } = useAuth();
  const { selectedStructure, selectedSupplyChain, selectedProducts, selectedProjects, selectedBuildings } = useContextProvider();
  const navigate = useNavigate();
  const location = useLocation();
  const [unreadCount, setUnreadCount] = useState(0);
  const [fetchedCompanyName, setFetchedCompanyName] = useState("");
  const [isLoadingCompany, setIsLoadingCompany] = useState(true);

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleOpenMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleProfileBoxAction = (event) => {
    const isCurrentlyOnProfile = location.pathname.startsWith(routing.profile.root);

    if (isCurrentlyOnProfile) {
      // Direct toggle back if already on profile page
      const previousLocation = localStorage.getItem("previousLocationBeforeProfile");
      if (previousLocation && !previousLocation.startsWith(routing.profile.root)) {
        navigate(previousLocation);
      } else {
        navigate(routing.sector.products.dashboard);
      }
    } else {
      // Otherwise open menu as usual
      handleOpenMenu(event);
    }
  };

  const handleNavigate = (path, state = {}) => {
    const isCurrentlyOnProfile = location.pathname.startsWith(routing.profile.root);
    const isTargetingGeneralProfile = path === routing.profile.account_settings && (state.activeTab === undefined || state.activeTab === 0);

    if (isCurrentlyOnProfile && isTargetingGeneralProfile) {
      // Toggle back if clicking general profile item while already there
      const previousLocation = localStorage.getItem("previousLocationBeforeProfile");
      if (previousLocation && !previousLocation.startsWith(routing.profile.root)) {
        navigate(previousLocation);
      } else {
        navigate(routing.sector.products.dashboard);
      }
    } else {
      // Store current location before leaving for profile for the first time
      if (!isCurrentlyOnProfile && path?.startsWith(routing.profile.root)) {
        localStorage.setItem("previousLocationBeforeProfile", location.pathname);
      }
      navigate(path, { state });
    }
    handleCloseMenu();
  };

  // Fetch unread notification count
  const fetchUnreadCount = () => {
    Notification.count_mine_unread()
      .then(res => res.json())
      .then(count => setUnreadCount(count))
      .catch(err => console.error("Failed to fetch notification count", err));
  };

  const fetchCompanyInfo = () => {
    setIsLoadingCompany(true);
    Company.get_mine()
      .then(res => res.json())
      .then(data => {
        if (data && data.length > 0) {
          setFetchedCompanyName(data[0].name);
        }
      })
      .catch(err => console.error("Failed to fetch company info", err))
      .finally(() => setIsLoadingCompany(false));
  };

  useEffect(() => {
    fetchUnreadCount();
    fetchCompanyInfo();
    const interval = setInterval(() => {
      fetchUnreadCount();
    }, 30000);
    window.addEventListener('notificationUpdate', fetchUnreadCount);
    return () => {
      clearInterval(interval);
      window.removeEventListener('notificationUpdate', fetchUnreadCount);
    };
  }, []);


  const getPageInfo = (path) => {
    const exactMapping = {
      [routing.sector.products.dashboard]: { title: t("Product LCA Dashboard"), desc: t("Comprehensive lifecycle assessment overview and sustainability metrics") },
      [routing.sector.projects.dashboard]: { title: t("Project LCA Dashboard"), desc: t("Track sustainability metrics and carbon footprints across your projects") },
      [routing.sector.buildings.dashboard]: { title: t("Building LCA Dashboard"), desc: t("Building lifecycle assessment and real estate sustainability metrics") },
      [routing.organization.dashboard]: { title: t("Organization Overview"), desc: t("Centralized command center for your company's ecosystem and intelligence") },
      [routing.organization.supply_chain.dashboard]: { title: t("Supply Chain Intelligence"), desc: t("Map value chains and track supplier carbon footprints across the network"), canGoBack: true, backPath: routing.organization.dashboard },
      [routing.organization.structure.dashboard]: { title: t("Organization Structure"), desc: t("Manage corporate facilities, legal entities, and operational sites"), canGoBack: true, backPath: routing.organization.dashboard },
      [routing.organization.corporate.dashboard]: { title: t("Corporate Dashboard"), desc: t("Analyze enterprise-level sustainability metrics and key performance indicators"), canGoBack: true, backPath: routing.organization.dashboard },
      [routing.engagement.collaborate.dashboard]: { title: t("Collaborate Dashboard"), desc: t("Shared workspace for multi-party sustainability projects and data exchange"), canGoBack: true, backPath: routing.engagement.dashboard },
      [routing.engagement.supplier_directory.dashboard]: { title: t("Supplier Intelligence Dashboard"), desc: t("Strategic overview of partner sustainability performance"), canGoBack: true, backPath: routing.engagement.dashboard },
      [routing.engagement.client_directory.dashboard]: { title: t("Client Intelligence Dashboard"), desc: t("Strategic overview of shared carbon impact with your clients"), canGoBack: true, backPath: routing.engagement.dashboard },

      // Supplier Directory Suite
      [routing.engagement.supplier_directory.dashboard]: { title: t("Supplier Intelligence Dashboard"), desc: t("Strategic overview of partner sustainability performance") },
      [routing.engagement.supplier_directory.submit_data]: { title: t("Submit Supplier Data"), desc: t("Share your carbon footprint data with a client partner") },
      [routing.engagement.supplier_directory.my_submissions]: { title: t("Supplier Submission Log"), desc: t("Manage and monitor all incoming data submissions and supplier records") },
      [routing.engagement.supplier_directory.comparison]: { title: t("Supplier Comparison"), desc: t("Precision multi-dimensional benchmarking between partner portfolios") },
      [routing.engagement.supplier_directory.reports]: { title: t("Supplier Reports Hub"), desc: t("Comprehensive diagnostic overview of upstream sustainability data") },

      // Client Directory Suite
      [routing.engagement.client_directory.dashboard]: { title: t("Client Intelligence Dashboard"), desc: t("Strategic overview of shared carbon impact with your clients") },
      [routing.engagement.client_directory.submit_data]: { title: t("Submit Client Data"), desc: t("Share your carbon footprint data with your downstream clients") },
      [routing.engagement.client_directory.my_submissions]: { title: t("Client Submission Log"), desc: t("Manage and monitor all outgoing data submissions and client records") },
      [routing.engagement.client_directory.comparison]: { title: t("Client Comparison"), desc: t("Precision multi-dimensional benchmarking between shared client portfolios") },
      [routing.engagement.client_directory.comparison]: { title: t("Client Comparison"), desc: t("Precision multi-dimensional benchmarking between shared client portfolios") },
      [routing.engagement.client_directory.reports]: { title: t("Client Reports Hub"), desc: t("Comprehensive diagnostic overview of outgoing compliance data") },
      [routing.engagement.client_directory.pending_responses]: { title: t("Pending Responses"), desc: t("Review and track your outgoing data submissions awaiting client approval"), canGoBack: true, backPath: routing.engagement.client_directory.dashboard },

      [routing.engagement.b2b_directory.dashboard]: { title: t("B2B Marketplace"), desc: t("Discover and connect with sustainable partners in the Arivu network") },
      [routing.digital_passport.dashboard]: { title: t("Digital Product Passport"), desc: t("Manage and track digital identities for your sustainable products") },
      [routing.usage.dashboard]: { title: t("Usage Analytics"), desc: t("Monitor platform usage, resource consumption, and engagement metrics") },
      [routing.admin.dashboard]: { title: t("Platform Administration"), desc: t("Manage system users, organizations, and global templates") },
      [routing.profile.dashboard]: { title: t("User Profile"), desc: t("Manage your account settings, security, and personal preferences") },
      [routing.setting.dashboard]: { title: t("System Settings"), desc: t("Configure platform-wide preferences, localization, and defaults") },
      [routing.support.dashboard]: { title: t("Support Center"), desc: t("Get help, view documentation, and contact the support team") },
      [routing.organization.sustainability.net_zero]: { title: t("Sustainability Dashboard"), desc: t("Track Net Zero paths and corporate decarbonization targets") },
      [routing.organization.compliance.sbti]: { title: t("Compliance Hub"), desc: t("Alignment with global regulatory frameworks and sustainability standards") },
      [routing.library.dashboard]: { title: t("Template Library"), desc: t("Manage and explore pre-built sustainability and LCA templates") },
      [routing.library.prebuilt_template]: { title: t("Prebuilt Templates"), desc: t("Prebuilt Templates") },
      [routing.library.epd]: { title: t("EPD Library"), desc: t("Verify and manage Environmental Product Declarations (EPDs)"), canGoBack: true, backPath: routing.library.dashboard },
      [routing.admin.dashboard + "/validation"]: { title: t("Validation Management"), desc: t("Quality assurance and compliance review system") },
      [routing.library.archive]: { title: t("Intra Company Library"), desc: t(" Manage Intra Company Library"), canGoBack: true, backPath: routing.library.dashboard },
      [routing.notification.dashboard]: { title: t("Notifications"), desc: t("Stay updated on your sustainability ecosystem and partner updates") },
      [routing.profile.account_settings]: { title: t("Account Settings"), desc: t("Manage your profile security, credentials, and preferences") },
      [routing.organization.supply_chain.comparison]: { title: t("Supply Chain Comparison"), desc: t("Benchmarking and comparative analysis across supply chain carbon footprints"), canGoBack: true, backPath: routing.organization.supply_chain.dashboard },
      [routing.organization.structure.comparison]: { title: t("Organization Structure Comparison"), desc: t("Benchmarking and comparative analysis across organizational structure footprints"), canGoBack: true, backPath: routing.organization.structure.dashboard },
      [routing.organization.corporate.comparison]: { title: t("Corporate Comparison"), desc: t("Benchmarking and comparative analysis across corporate carbon footprints"), canGoBack: true, backPath: routing.organization.corporate.dashboard },
      [routing.sector.products.comparison]: { title: t("Product Comparison"), desc: t("Benchmarking and comparative environmental analysis of products"), canGoBack: true, backPath: routing.sector.products.dashboard },
      [routing.sector.projects.comparison]: { title: t("Project Comparison"), desc: t("Track and compare sustainability performance across projects"), canGoBack: true, backPath: routing.sector.projects.dashboard },
      [routing.sector.products.digital_passport]: { title: t("Product Digital Passport"), desc: t("EU regulation compliant digital identity for sustainable products"), canGoBack: true, backPath: routing.sector.products.dashboard },
      [routing.sector.projects.digital_passport]: { title: t("Project Digital Passport"), desc: selectedProjects?.[0]?.name || t("EU regulation compliant digital identity for sustainable projects"), canGoBack: true, backPath: routing.sector.projects.dashboard },
      [routing.sector.buildings.digital_passport]: { title: t("Building Digital Passport"), desc: selectedBuildings?.[0]?.name || t("EU regulation compliant digital identity for sustainable buildings"), canGoBack: true, backPath: routing.sector.buildings.dashboard },
      [routing.sector.products.reuse_workspace]: { title: t("Reuse Workspace"), desc: selectedProducts?.[0]?.name || t("Manage reusing lifecycle assessment"), canGoBack: true, backPath: routing.sector.products.dashboard },
      [routing.sector.products.circularity]: { 
        title: t("Product Circularity"), 
        desc: selectedProducts?.[0]?.name || t("Environmental impact assessment and material circularity index"), 
        canGoBack: true, 
        backPath: routing.sector.products.dashboard 
      },
      [routing.sector.products.supplier_portal]: { 
        title: t("Product Supplier Portal"), 
        desc: selectedProducts?.[0]?.name || t("Environmental and circularity analysis of product suppliers"), 
        canGoBack: true, 
        backPath: routing.sector.products.dashboard 
      },
      [routing.sector.projects.circularity]: { 
        title: t("Project Circularity"), 
        desc: selectedProjects?.[0]?.name || t("Environmental impact assessment and material circularity index"), 
        canGoBack: true, 
        backPath: routing.sector.projects.dashboard 
      },
      [routing.sector.projects.supplier_portal]: { 
        title: t("Project Supplier Portal"), 
        desc: selectedProjects?.[0]?.name || t("Environmental and circularity analysis of project suppliers"), 
        canGoBack: true, 
        backPath: routing.sector.projects.dashboard 
      },
      [routing.sector.buildings.circularity]: { 
        title: t("Building Circularity"), 
        desc: selectedBuildings?.[0]?.name || t("Environmental impact assessment and material circularity index"), 
        canGoBack: true, 
        backPath: routing.sector.buildings.dashboard 
      },
    };

    if (exactMapping[path]) return exactMapping[path];

    // Fallbacks
    if (path.includes("/structure/workspace")) return {
      title: t("Organization Workspace"),
      desc: selectedStructure?.name || t("Organization Workspace"),
      canGoBack: true,
      backPath: routing.organization.structure.dashboard
    };
    if (path.includes("/supply_chain/workspace")) return {
      title: t("Supply Chain Workspace"),
      desc: selectedSupplyChain?.name || t("Partner Asset"),
      canGoBack: true,
      backPath: routing.organization.supply_chain.dashboard
    };

    if (path.includes("/products/workspace")) return {
      title: t("Product Workspace"),
      desc: selectedProducts?.[0]?.name || t("Detailed lifecycle assessment view"),
      canGoBack: true,
      backPath: routing.sector.products.dashboard
    };
    if (path.includes("/projects/workspace")) return {
      title: t("Project Workspace"),
      desc: selectedProjects?.[0]?.name || t("Project sustainability analysis"),
      canGoBack: true,
      backPath: routing.sector.projects.dashboard
    };
    if (path.includes("/buildings/workspace")) return {
      title: t("Building Workspace"),
      desc: selectedBuildings?.[0]?.name || t("Building footprint analysis"),
      canGoBack: true,
      backPath: routing.sector.buildings.dashboard
    };


    return null;
  };

  const pageInfo = getPageInfo(location.pathname);

  return (
    <HeaderContainer>
      {/* LEFT SIDE: Page Title & Info */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2.5, animation: "fadeInUp 0.5s ease" }}>
        {pageInfo && (
          <Box
            onClick={() => pageInfo?.canGoBack && navigate(pageInfo.backPath)}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              textAlign: "left",
              cursor: pageInfo?.canGoBack ? "pointer" : "default",
              transition: "all 0.2s ease"
            }}
          >
            <Typography
              className="header-title"
              sx={{
                fontSize: "1.35rem",
                fontWeight: 600, // Balanced weight
                color: m3.onSurface,
                letterSpacing: "-0.015em",
                lineHeight: 1.1,
              }}
            >
              {pageInfo.title}
            </Typography>
            {pageInfo.desc &&
              pageInfo.desc.trim().toLowerCase() !== pageInfo.title?.trim().toLowerCase() &&
              !(
                pageInfo.title?.toLowerCase().includes("circularity") &&
                ["product circularity", "project circularity", "building circularity"].includes(pageInfo.desc.trim().toLowerCase())
              ) && (
              <Typography
                className="header-desc"
                sx={{
                  fontSize: "0.92rem",
                  color: m3.onSurfaceVariant,
                  fontWeight: 400,
                  lineHeight: 1.2,
                  marginTop: "2px",
                  letterSpacing: "0.01em"
                }}
              >
                {pageInfo.desc}
              </Typography>
            )}
          </Box>
        )}
      </Box>

      {/* RIGHT SIDE: User Profile & Tagline */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5, // Reduced from 3 to tighten up spacing between text, divider, and profile
          height: "100%",
          pr: 0.5,
          justifyContent: "flex-end"
        }}
      >
        {/* Tagline */}
        <Typography
          sx={{
            display: { xs: "none", lg: "flex" },
            alignItems: "baseline",
            gap: 1.5,
          }}
        >
          <span style={{ color: M3_BRAND.neutral, fontSize: "1.2rem", fontWeight: 500 }}>{t("Know")}</span>
          <span style={{ color: m3.outlineVariant }}>•</span>
          <span style={{ color: M3_BRAND.primary, fontSize: "1.2rem", fontWeight: 500 }}>{t("Act")}</span>
          <span style={{ color: m3.outlineVariant }}>•</span>
          <span style={{ color: m3.onPrimaryContainer, fontSize: "1.2rem", fontWeight: 500 }}>{t("Inspire")}</span>
        </Typography>

        <Divider orientation="vertical" flexItem sx={{ height: 32, alignSelf: "center", ml: 4, mr: -1 }} />

        <Box
          onClick={handleProfileBoxAction}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            cursor: "pointer",
            p: "6px 14px",
            borderRadius: "12px",
            transition: "all 0.2s ease-in-out",
            border: `1px solid ${open ? m3.outlineVariant : "transparent"}`,
            bgcolor: open ? m3.stateHover : "transparent",
            "&:hover": {
              bgcolor: m3.stateHover,
              transform: "translateY(-1px)"
            }
          }}
        >
          <Badge
            badgeContent={unreadCount}
            color="error"
            sx={{
              "& .MuiBadge-badge": {
                fontSize: "0.6rem",
                height: "16px",
                minWidth: "16px",
                fontWeight: 900,
                top: 4,
                right: 4,
                border: "2px solid white"
              }
            }}
          >
            <Avatar
              sx={{
                width: 36,
                height: 36,
                bgcolor: "primary.main",
                fontSize: "18px",
                fontWeight: 600,
                boxShadow: 1,
              }}
            >
              {username ? username.charAt(0).toUpperCase() : "U"}
            </Avatar>
          </Badge>

          <Typography
            sx={{
              fontSize: "0.7rem",
              fontWeight: 700,
              color: "primary.main",
              letterSpacing: "0.01em",
              fontFamily: "'Poppins', sans-serif"
            }}
          >
            {username || user?.username || t("User")}
          </Typography>

          <LuChevronDown
            size={18}
            color={m3.onSurfaceVariant}
            style={{
              transition: "transform 0.3s ease",
              transform: open ? "rotate(180deg)" : "rotate(0)"
            }}
          />
        </Box>

        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleCloseMenu}
          onClick={handleCloseMenu}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          transitionDuration={200}
          PaperProps={{
            elevation: 0,
            sx: {
              overflow: 'visible',
              boxShadow: 3,
              mt: 1.5,
              minWidth: 220,
              borderRadius: "14px",
              border: `1px solid ${m3.outlineVariant}`,
              p: 1, // Add internal padding for buttons
              '&:before': {
                content: '""',
                display: 'block',
                position: 'absolute',
                top: 0,
                right: 32,
                width: 10,
                height: 10,
                bgcolor: 'background.paper',
                transform: 'translateY(-50%) rotate(45deg)',
                zIndex: 0,
              },
            },
          }}
        >
          <MenuItem onClick={() => handleNavigate(routing.profile.account_settings, { activeTab: 0 })} sx={{ py: 1.2, px: 2, borderRadius: "10px" }}>
            <ListItemIcon>
              <LuSettings size={18} />
            </ListItemIcon>
            <Typography variant="body2" fontWeight={600}>{t("Settings")}</Typography>
          </MenuItem>

          <MenuItem onClick={() => handleNavigate(routing.support.dashboard)} sx={{ py: 1.2, px: 2, borderRadius: "10px" }}>
            <ListItemIcon>
              <LuLifeBuoy size={18} />
            </ListItemIcon>
            <Typography variant="body2" fontWeight={600}>{t("Support")}</Typography>
          </MenuItem>

          <MenuItem onClick={() => handleNavigate(routing.profile.account_settings, { activeTab: 3 })} sx={{ py: 1.2, px: 2, borderRadius: "10px" }}>
            <ListItemIcon>
              <LuBell size={18} />
            </ListItemIcon>
            <Typography variant="body2" fontWeight={600}>{t("Notifications")}</Typography>
          </MenuItem>

          <MenuItem
            onClick={async () => {
              handleCloseMenu();
              await clearUserCache();
              window.location.reload();
            }}
            sx={{ py: 1.2, px: 2, borderRadius: "10px" }}
          >
            <ListItemIcon>
              <LuRefreshCw size={18} />
            </ListItemIcon>
            <Typography variant="body2" fontWeight={600}>{t("System Refresh")}</Typography>
          </MenuItem>

          <Divider sx={{ my: 1, opacity: 0.4 }} />

          <MenuItem
            onClick={() => attemptLogout()}
            sx={{
              py: 1.2,
              px: 2,
              color: "primary.main",
              borderRadius: "10px",
              mt: 0.5,
              "&:hover": {
                bgcolor: m3.stateSelected,
                transform: "translateX(4px)"
              },
              transition: "all 0.2s"
            }}
          >
            <ListItemIcon>
              <LuLogOut size={18} style={{ color: M3_BRAND.primary }} />
            </ListItemIcon>
            <Typography variant="body2" fontWeight={800}>{t("Sign Out")}</Typography>
          </MenuItem>
        </Menu>
      </Box>
    </HeaderContainer>
  );
}

export default Header;
